import React, { useState } from 'react';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EditNoteIcon from '@mui/icons-material/EditNote';

import EditModal from '../modals/EditModal';
import MultiSelectField from '../../common/fields/MultiSelectField';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import AppSnackbar from '../../common/popups/AppSnackbar';
import { useMasterData } from '../../../contexts/MasterDataContext';
import useProfileSection from '../../../hooks/useProfileSection';

const formatFeeAmount = (value) => {
  const normalized = Number(String(value ?? '').replace(/,/g, '').trim());

  if (!Number.isFinite(normalized)) return 'Rs. 0';

  return `Rs. ${normalized.toLocaleString('en-LK')}`;
};

function OverviewSection({ teacher, canEdit, onUpdate, teacherFees = [], feesLoading = false, feesError = '' }) {
  const { isEditing, openModal, closeModal, save, errors, loading, snackbarProps } =
    useProfileSection(teacher.profileId, onUpdate);
  const {subjectOptions, mediumOptions, cityOptions, classTypeOptions } =
    useMasterData();

  const [modalData, setModalData] = useState({
    subjects: teacher.subjects.map((s) => s.id),
    medium: teacher.medium.map((m) => m.id),
    classTypes: teacher.classTypes.map((c) => c.id),
    cities: teacher.cities.map((c) => c.id),
    subjectCategory: teacher.subjectCategory ?? '',
  });

  const getOptionsByIds = (ids, options) => options.filter((o) => ids.includes(o.id));

  const handleSave = (e) => {
    e.preventDefault();
    save(modalData, { successMessage: 'Overview updated!' });
  };

  return (
    <div className="bg-gradient-glass rounded-xl p-10 mb-6 h-full">
      <div className="flex justify-between items-start mb-6">
        <h2 className="uppercase text-2xl flex items-center">
          <span className="bg-gradient-primary w-2 h-8 mr-3 rounded-full"></span>
          OVERVIEW
        </h2>
        {canEdit && (
          <button onClick={openModal} className="text-primary hover:text-orange-400">
            <EditNoteIcon fontSize="large" />
          </button>
        )}
      </div>

      <hr className="mt-5 mb-8 border-white/20 w-full" />

      <div className="space-y-6">
        <div>
          <div className="flex items-center mb-3">
            <TaskAltIcon className="mr-2 text-primary" />
            <span className="font-semibold">Subjects</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {getOptionsByIds(
              teacher.subjects.map((s) => s.id),
              subjectOptions,
            ).length > 0 ? (
              getOptionsByIds(
                teacher.subjects.map((s) => s.id),
                subjectOptions,
              ).map((s, i) => (
                <span key={s.id} className="text-gray-400 text-sm">
                  {s.name}
                  {i < teacher.subjects.length - 1 ? ' |' : ''}
                </span>
              ))
            ) : (
              <span className="text-gray-500 text-sm">No subjects added</span>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center mb-3">
            <TaskAltIcon className="mr-2 text-primary" />
            <span className="font-semibold">Medium</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {getOptionsByIds(
              teacher.medium.map((m) => m.id),
              mediumOptions,
            ).length > 0 ? (
              getOptionsByIds(
                teacher.medium.map((m) => m.id),
                mediumOptions,
              ).map((m, i) => (
                <span key={m.id} className="text-gray-400 text-sm">
                  {m.name}
                  {i < teacher.medium.length - 1 ? ' |' : ''}
                </span>
              ))
            ) : (
              <span className="text-gray-500 text-sm">No teaching medium added</span>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center mb-3">
            <TaskAltIcon className="mr-2 text-primary" />
            <span className="font-semibold">Class Type</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {getOptionsByIds(
              teacher.classTypes.map((c) => c.id),
              classTypeOptions,
            ).map((c) => (
              <span key={c.id} className="px-4 py-2 bg-gray-800 text-gray-300 rounded-full text-sm">
                {c.name}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center mb-3">
            <TaskAltIcon className="mr-2 text-primary" />
            <span className="font-semibold">
              Cities <span className="">(If home visit)</span>
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {getOptionsByIds(
              teacher.cities.map((c) => c.id),
              cityOptions,
            ).map((c) => (
              <span key={c.id} className="px-4 py-2 bg-gray-800 text-gray-300 rounded-full text-sm">
                {c.name}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center mb-3">
            <TaskAltIcon className="mr-2 text-primary" />
            <span className="font-semibold">Fees</span>
          </div>

          {feesLoading ? (
            <div className="flex min-h-[72px] items-center justify-center rounded-lg border border-white/10 bg-white/5">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : feesError ? (
            <p className="text-sm text-gray-400">{feesError}</p>
          ) : teacherFees.length > 0 ? (
            <div className="space-y-3">
              {teacherFees.map((fee) => (
                <div
                  key={fee.id ?? `${fee.gradeTypeDesc}-${fee.fee}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3"
                >
                  <span className="text-sm text-gray-200">
                    {fee.gradeTypeDesc || fee.gradeName || 'Unknown'}
                  </span>
                  <span className="text-sm text-gray-200">{fee.gradeName}</span>
                  <span className="rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-sm font-semibold text-primary">
                    {formatFeeAmount(fee.fee)} (per {fee.feeForLabel})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-gray-500 text-sm">No fees added yet.</span>
          )}
        </div>
      </div>

      <EditModal isOpen={isEditing} onClose={closeModal} title="Edit Overview">
        <form onSubmit={handleSave}>
          <div className="space-y-6 mb-10">
            {errors.subjects && <p className="text-red-400 text-sm">{errors.subjects}</p>}
            <MultiSelectField
              label="Subjects"
              value={modalData.subjects}
              onChange={(s) => setModalData({ ...modalData, subjects: s })}
              options={subjectOptions}
              required
              error={!!errors.subjects}
              helperText={errors.subjects}
            />
            <MultiSelectField
              label="Medium"
              value={modalData.medium}
              onChange={(m) => setModalData({ ...modalData, medium: m })}
              options={mediumOptions}
              required
              error={!!errors.medium}
              helperText={errors.medium}
            />
            <MultiSelectField
              label="Class Types"
              value={modalData.classTypes}
              onChange={(t) => setModalData({ ...modalData, classTypes: t })}
              options={classTypeOptions}
              required
              error={!!errors.classTypes}
              helperText={errors.classTypes}
            />
            <MultiSelectField
              label="Cities"
              value={modalData.cities}
              onChange={(c) => setModalData({ ...modalData, cities: c })}
              options={cityOptions}
              error={!!errors.cities}
              helperText={errors.cities}
            />
          </div>
          <div className="flex justify-end">
            <PrimaryButton className="w-full sm:w-auto" type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </PrimaryButton>
          </div>
        </form>
      </EditModal>

      <AppSnackbar {...snackbarProps} />
    </div>
  );
}

export default OverviewSection;
